import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';

import { CashApplicationState } from '../globalSlices/cashApplicationSlice';
import {
  isRecordInExceptions,
  isRecordSettledForPosting,
  RemittanceRecord,
} from '@dill/dill-shared';
import {
  fetchRemittanceRecordsThunk,
  fetchCashReceiptsThunk,
  fetchOpenInvoicesThunk,
  processUploadedFileThunk,
  applyPaymentThunk,
  postUnappliedThunk,
  approveDepositGroupThunk,
  approveOneClickThunk,
  deleteRemittanceRecordThunk,
  updateCashAppSettingsThunk,
  fetchBankFeedThunk,
  createBankFeedLinkTokenThunk,
  connectBankFeedThunk,
  disconnectBankFeedThunk,
  syncBankFeedNowThunk,
  fetchEmailIngestionThunk,
  enableEmailIngestionThunk,
  disableEmailIngestionThunk,
  fetchRemittanceEmailsNowThunk,
  fetchBankTransactionsThunk,
  linkBankTransactionThunk,
  unlinkBankTransactionThunk,
  updateRemittanceAllocationsThunk,
  dismissBankTransactionThunk,
} from '../services/cashApplicationService';

export const fetchRemittanceRecordsExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'fetchRemittanceRecords';
  builder
    .addCase(fetchRemittanceRecordsThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(fetchRemittanceRecordsThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      const records = action.payload;

      const oneClick: RemittanceRecord[] = [];
      const exceptions: RemittanceRecord[] = [];
      const awaitingSettlement: RemittanceRecord[] = [];

      // Every record, posted ones included, before the queue routing drops them. Post History reads
      // its per-payment detail from here — a receipt carries none of its own.
      state.remittanceRecordsById = Object.fromEntries(
        records.map((record) => [record.id, record])
      );

      /**
       * One record, one queue, and the bank question is asked first.
       *
       * Settlement outranks allocation. Until the money is confirmed there is nothing to apply,
       * so a record with no usable allocation waits in Awaiting Bank alongside everything else
       * rather than asking someone to work out where to put cash that may never arrive.
       *
       * Once a deposit is linked the record splits on whether we can allocate it: an
       * unallocatable one becomes an Exception, everything else goes to 1-Click Review. So both
       * of those tabs contain confirmed money only, and every question they ask is answerable.
       *
       * NOT_APPLICABLE goes to 1-Click Review, not Awaiting Bank. It means the supplier has no bank
       * feed, so nothing could ever confirm the payment — and since posting now requires
       * confirmation, parking those records in a queue with no way out would leave that supplier
       * unable to post at all. `isRecordSettledForPosting` is the same rule the server refuses by.
       */
      for (const record of records) {
        if (
          record.matchStatus !== 'PENDING' &&
          record.matchStatus !== 'AUTO_MATCHED' &&
          record.matchStatus !== 'REVIEW_NEEDED'
        ) {
          // APPLIED and MANUALLY_MATCHED are done — they belong to Post History. This is also
          // what keeps records posted before the settlement gate existed, which still carry a
          // stale PENDING settlement, out of the Awaiting Bank queue.
          continue;
        }

        // PENDING, AMBIGUOUS and FAILED mean the bank has not confirmed this money yet and might
        // still. NOT_APPLICABLE means it never will, which is a different answer.
        if (!isRecordSettledForPosting(record)) {
          awaitingSettlement.push(record);
        } else if (isRecordInExceptions(record)) {
          exceptions.push(record);
        } else {
          oneClick.push(record);
        }
      }

      state.oneClickReviewItems = oneClick;
      state.exceptionItems = exceptions;
      state.awaitingSettlementItems = awaitingSettlement;
    })
    .addCase(fetchRemittanceRecordsThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to fetch records';
    });
};

export const fetchCashReceiptsExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'fetchCashReceipts';
  builder
    .addCase(fetchCashReceiptsThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(fetchCashReceiptsThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.postHistory = action.payload;
    })
    .addCase(fetchCashReceiptsThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to fetch receipts';
    });
};

export const fetchOpenInvoicesExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'fetchOpenInvoices';
  builder
    .addCase(fetchOpenInvoicesThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(fetchOpenInvoicesThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.openInvoices = action.payload;
    })
    .addCase(fetchOpenInvoicesThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to fetch open invoices';
    });
};

export const processUploadedFileExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'processUploadedFile';
  builder
    .addCase(processUploadedFileThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(processUploadedFileThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      const { fileName, data } = action.payload;

      const file = state.manualUploadFiles.find((f) => f.name === fileName);
      if (file) {
        file.status = 'matched';
      }

      // Same routing as fetchRemittanceRecords, so a just-uploaded record lands in the queue
      // it would land in after a refresh rather than jumping tabs on the next fetch.
      for (const record of data.remittanceRecords) {
        // Merged, not replaced: this is one upload's worth of records, not the supplier's.
        state.remittanceRecordsById[record.id] = record;
        if (record.matchStatus === 'APPLIED') {
          // Auto-posted by backend — skip queues, will appear in postHistory
          continue;
        }
        if (!isRecordSettledForPosting(record)) {
          state.awaitingSettlementItems.unshift(record);
        } else if (isRecordInExceptions(record)) {
          state.exceptionItems.unshift(record);
        } else {
          state.oneClickReviewItems.unshift(record);
        }
      }
    })
    .addCase(processUploadedFileThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Processing failed';

      const fileName = action.meta?.arg?.fileName;
      if (fileName) {
        const file = state.manualUploadFiles.find((f) => f.name === fileName);
        if (file) {
          file.status = 'error';
        }
      }
    });
};

export const applyPaymentExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'applyPayment';
  builder
    .addCase(applyPaymentThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(applyPaymentThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      delete state.pendingAllocations[action.payload.remittanceId];
    })
    .addCase(applyPaymentThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to apply payment';
    });
};

export const postUnappliedExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'postUnapplied';
  builder
    .addCase(postUnappliedThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(postUnappliedThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      delete state.pendingAllocations[action.payload.remittanceId];
    })
    .addCase(postUnappliedThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to post unapplied';
    });
};

export const approveOneClickExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'approveOneClick';
  builder
    .addCase(approveOneClickThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(approveOneClickThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      delete state.pendingAllocations[action.payload.remittanceId];
    })
    .addCase(approveOneClickThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to approve payment';
    });
};

export const approveDepositGroupExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'approveDepositGroup';
  builder
    .addCase(approveDepositGroupThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(approveDepositGroupThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      // One receipt covering every payment that posted, so its record ids are exactly the edits
      // that are now committed and no longer pending.
      action.payload.receipt.remittanceIds.forEach((remittanceId) => {
        delete state.pendingAllocations[remittanceId];
      });
    })
    .addCase(approveDepositGroupThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to approve the payments on this deposit';
    });
};

/**
 * Hydrates cash app settings from the supplier document, which is the source of truth.
 *
 * Matched on the action type string rather than importing the auth thunk: the auth
 * service sits upstream of the store, and importing it here would close an import cycle
 * through cashApplicationSlice.
 */
export const hydrateSettingsFromSupplierExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  builder.addMatcher(
    (action) => action.type === 'auth/getCurrentSupplier/fulfilled',
    (state, action: any) => {
      const supplier = action.payload?.success ? action.payload.data : null;
      const cashAppSettings = supplier?.cashAppSettings ?? null;
      if (cashAppSettings) {
        state.settings = { ...state.settings, ...cashAppSettings };
      }
    }
  );
};

export const updateCashAppSettingsExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'updateCashAppSettings';
  builder
    .addCase(updateCashAppSettingsThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(updateCashAppSettingsThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.settings = action.payload.settings;
    })
    .addCase(updateCashAppSettingsThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to save settings';
    });
};

export const bankFeedExtraReducers = (builder: ActionReducerMapBuilder<CashApplicationState>) => {
  // One reducer for the whole feed: fetch, connect and disconnect all resolve to the same
  // status shape, so they share a slot rather than repeating four near-identical blocks.
  const statusThunks = [fetchBankFeedThunk, connectBankFeedThunk, disconnectBankFeedThunk];

  statusThunks.forEach((thunk) => {
    const reducerId = thunk.typePrefix;
    builder
      .addCase(thunk.pending, (state) => {
        state.loadingList = [...new Set([...state.loadingList, reducerId])];
        state.error = null;
      })
      .addCase(thunk.fulfilled, (state, action) => {
        state.loadingList = state.loadingList.filter((item) => item !== reducerId);
        state.bankFeed = action.payload;
      })
      .addCase(thunk.rejected, (state, action) => {
        state.loadingList = state.loadingList.filter((item) => item !== reducerId);
        state.error = (action.payload as string) || 'Bank feed request failed';
      });
  });

  const syncId = 'syncBankFeedNow';
  builder
    .addCase(syncBankFeedNowThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, syncId])];
      state.error = null;
    })
    .addCase(syncBankFeedNowThunk.fulfilled, (state) => {
      state.loadingList = state.loadingList.filter((item) => item !== syncId);
    })
    .addCase(syncBankFeedNowThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== syncId);
      state.error = (action.payload as string) || 'Failed to sync bank feed';
    });

  const linkTokenId = 'createBankFeedLinkToken';
  builder
    .addCase(createBankFeedLinkTokenThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, linkTokenId])];
      state.error = null;
    })
    .addCase(createBankFeedLinkTokenThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== linkTokenId);
      state.bankFeedLinkToken = action.payload;
    })
    .addCase(createBankFeedLinkTokenThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== linkTokenId);
      state.error = (action.payload as string) || 'Failed to create bank link token';
    });
};

export const fetchEmailIngestionExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'fetchEmailIngestion';
  builder
    .addCase(fetchEmailIngestionThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(fetchEmailIngestionThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.emailIngestion = action.payload;
    })
    .addCase(fetchEmailIngestionThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to fetch email ingestion settings';
    });
};

export const enableEmailIngestionExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'enableEmailIngestion';
  builder
    .addCase(enableEmailIngestionThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(enableEmailIngestionThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.emailIngestion = action.payload;
    })
    .addCase(enableEmailIngestionThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to enable remittance email';
    });
};

export const disableEmailIngestionExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'disableEmailIngestion';
  builder
    .addCase(disableEmailIngestionThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(disableEmailIngestionThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.emailIngestion = action.payload;
    })
    .addCase(disableEmailIngestionThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to disable remittance email';
    });
};

export const fetchRemittanceEmailsNowExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'fetchRemittanceEmailsNow';
  builder
    .addCase(fetchRemittanceEmailsNowThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(fetchRemittanceEmailsNowThunk.fulfilled, (state) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    })
    .addCase(fetchRemittanceEmailsNowThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to check for new remittance emails';
    });
};

export const deleteRemittanceRecordExtraReducer = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const reducerId = 'deleteRemittanceRecord';
  builder
    .addCase(deleteRemittanceRecordThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.error = null;
    })
    .addCase(deleteRemittanceRecordThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      // An unsaved allocation must not outlive the record it was drafted against.
      delete state.pendingAllocations[action.payload.remittanceId];
    })
    .addCase(deleteRemittanceRecordThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.error = (action.payload as string) || 'Failed to delete payment';
    });
};

export const bankTransactionExtraReducers = (
  builder: ActionReducerMapBuilder<CashApplicationState>
) => {
  const fetchId = 'fetchBankTransactions';
  builder
    .addCase(fetchBankTransactionsThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, fetchId])];
      state.error = null;
    })
    .addCase(fetchBankTransactionsThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== fetchId);
      state.bankTransactions = action.payload;
    })
    .addCase(fetchBankTransactionsThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== fetchId);
      state.error = (action.payload as string) || 'Failed to fetch bank deposits';
    });

  // Both refetch on success, so there is nothing to reduce beyond the loading flag — the refetch
  // supplies the new truth.
  [linkBankTransactionThunk, unlinkBankTransactionThunk].forEach((thunk) => {
    const reducerId = thunk.typePrefix;
    builder
      .addCase(thunk.pending, (state) => {
        state.loadingList = [...new Set([...state.loadingList, reducerId])];
        state.error = null;
      })
      .addCase(thunk.fulfilled, (state) => {
        state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      })
      .addCase(thunk.rejected, (state, action) => {
        state.loadingList = state.loadingList.filter((item) => item !== reducerId);
        state.error = (action.payload as string) || 'Failed to save the change';
      });
  });

  // As above, but the stored allocation now outranks any browser-held draft of the same record —
  // leaving one would shadow what was just saved when the record surfaces in another queue.
  const allocationsId = updateRemittanceAllocationsThunk.typePrefix;
  builder
    .addCase(updateRemittanceAllocationsThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, allocationsId])];
      state.error = null;
    })
    .addCase(updateRemittanceAllocationsThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== allocationsId);
      delete state.pendingAllocations[action.payload.remittanceId];
    })
    .addCase(updateRemittanceAllocationsThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== allocationsId);
      state.error = (action.payload as string) || 'Failed to save the change';
    });

  const dismissId = 'dismissBankTransaction';
  builder
    .addCase(dismissBankTransactionThunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, dismissId])];
      state.error = null;
    })
    .addCase(dismissBankTransactionThunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== dismissId);
      // Dropped locally rather than refetched: the row is leaving the queue either way, and
      // this keeps the list from flickering while a round trip completes.
      state.bankTransactions = state.bankTransactions.filter(
        (record) => record.id !== action.payload.bankTransactionId
      );
    })
    .addCase(dismissBankTransactionThunk.rejected, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== dismissId);
      state.error = (action.payload as string) || 'Failed to dismiss the deposit';
    });
};
